import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  FaTimes,
  FaExclamationTriangle,
  FaCheckCircle,
  FaSync,
  FaPen,
  FaSearch,
} from "react-icons/fa";
import BtnLoader from "Components/Common/Loader/BtnLoader";
import Swal from "sweetalert2";

const severityConfig = {
  high: {
    bg: "bg-red-50",
    border: "border-red-200",
    badge: "bg-red-500 text-white",
    icon: "🚨",
  },
  medium: {
    bg: "bg-yellow-50",
    border: "border-yellow-200",
    badge: "bg-yellow-500 text-white",
    icon: "⚠️",
  },
  low: {
    bg: "bg-blue-50",
    border: "border-blue-200",
    badge: "bg-blue-400 text-white",
    icon: "ℹ️",
  },
};

const DiagnoseDoubleCountsModal = ({ open, onClose, event }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [fixingUser, setFixingUser] = useState(null);
  const [editMode, setEditMode] = useState(null); // userId being edited
  const [editValues, setEditValues] = useState({});

  useEffect(() => {
    if (open && event?._id) {
      fetchDiagnosis();
    }
  }, [open, event]);

  const fetchDiagnosis = () => {
    setLoading(true);
    setError(null);
    axios
      .get(
        `${process.env.REACT_APP_API_URL}/event/${event._id}/diagnose-double-counts`
      )
      .then((res) => {
        if (res.data.success) setData(res.data.data);
        else setError(res.data.message);
      })
      .catch((err) => setError(err.response?.data?.message || "Failed"))
      .finally(() => setLoading(false));
  };

  const handleRecalculate = (userId, nickName) => {
    Swal.fire({
      title: "Recalculate Points?",
      html: `This will recalculate <b>${nickName}</b>'s points from daily activity logs.<br/><br/>This is the safest fix — it resets points to match what was actually logged each day.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#c51369",
      confirmButtonText: "Yes, Recalculate",
    }).then((result) => {
      if (result.isConfirmed) {
        setFixingUser(userId);
        axios
          .post(
            `${process.env.REACT_APP_API_URL}/event/${event._id}/user/${userId}/correct-points`,
            { action: "recalculate" }
          )
          .then((res) => {
            if (res.data.success) {
              const diff = res.data.data.diff.totalPoints;
              Swal.fire({
                icon: "success",
                title: "Points Corrected!",
                html: `
                  <div style="text-align:left; font-size:14px">
                    <p><b>Before:</b> ${res.data.data.before.totalPoints} pts</p>
                    <p><b>After:</b> ${res.data.data.after.totalPoints} pts</p>
                    <p><b>Change:</b> <span style="color:${diff < 0 ? "red" : "green"}">${diff > 0 ? "+" : ""}${diff} pts</span></p>
                  </div>`,
                timer: 5000,
              });
              fetchDiagnosis();
            }
          })
          .catch((err) => {
            Swal.fire({
              icon: "error",
              title: "Error",
              text: err.response?.data?.message || "Failed to correct points.",
            });
          })
          .finally(() => setFixingUser(null));
      }
    });
  };

  const handleManualSave = (userId, nickName) => {
    Swal.fire({
      title: "Apply Manual Correction?",
      html: `Set custom point values for <b>${nickName}</b>.<br/>This will override the current breakdown.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#c51369",
      confirmButtonText: "Apply",
    }).then((result) => {
      if (result.isConfirmed) {
        setFixingUser(userId);
        axios
          .post(
            `${process.env.REACT_APP_API_URL}/event/${event._id}/user/${userId}/correct-points`,
            { action: "manual", manualPoints: editValues }
          )
          .then((res) => {
            if (res.data.success) {
              const diff = res.data.data.diff.totalPoints;
              Swal.fire({
                icon: "success",
                title: "Points Updated!",
                html: `<p>Change: <b>${diff > 0 ? "+" : ""}${diff}</b> pts</p>`,
                timer: 3000,
              });
              setEditMode(null);
              setEditValues({});
              fetchDiagnosis();
            }
          })
          .catch((err) => {
            Swal.fire({
              icon: "error",
              title: "Error",
              text: err.response?.data?.message || "Failed.",
            });
          })
          .finally(() => setFixingUser(null));
      }
    });
  };

  if (!open) return null;

  const filtered = data?.flaggedUsers?.filter((u) =>
    search
      ? u.user.nickName?.toLowerCase().includes(search.toLowerCase()) ||
        u.user.phoneNumber?.includes(search)
      : true
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 z-10 border-b bg-gradient-to-r from-red-600 to-orange-500 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="flex items-center gap-2 text-xl font-bold text-white">
                <FaExclamationTriangle /> Diagnose Double-Counted Points
              </h2>
              <p className="mt-1 text-sm text-white/80">
                {event?.name} — Compares breakdown totals vs daily activity logs
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={fetchDiagnosis}
                className="rounded-full bg-white/20 p-2 text-white transition hover:bg-white/30"
                title="Re-scan"
              >
                <FaSync className={loading ? "animate-spin" : ""} />
              </button>
              <button
                onClick={onClose}
                className="rounded-full bg-white/20 p-2 text-white transition hover:bg-white/30"
              >
                <FaTimes />
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <BtnLoader />
            <span className="ml-3 text-gray-500">
              Scanning all participants...
            </span>
          </div>
        ) : error ? (
          <div className="py-20 text-center text-red-500">{error}</div>
        ) : data ? (
          <div className="p-6">
            {/* Stats */}
            <div className="mb-6 grid grid-cols-3 gap-4">
              <div className="rounded-lg bg-gray-50 p-4 text-center">
                <p className="text-2xl font-bold text-gray-700">
                  {data.totalParticipants}
                </p>
                <p className="text-xs text-gray-500">Total Participants</p>
              </div>
              <div className="rounded-lg bg-red-50 p-4 text-center">
                <p className="text-2xl font-bold text-red-600">
                  {data.flaggedCount}
                </p>
                <p className="text-xs text-gray-500">Flagged Users</p>
              </div>
              <div className="rounded-lg bg-green-50 p-4 text-center">
                <p className="text-2xl font-bold text-green-600">
                  {data.totalParticipants - data.flaggedCount}
                </p>
                <p className="text-xs text-gray-500">Clean Users</p>
              </div>
            </div>

            {data.flaggedCount === 0 ? (
              <div className="py-16 text-center">
                <FaCheckCircle className="mx-auto mb-3 text-5xl text-green-500" />
                <p className="text-lg font-medium text-green-700">
                  All clear! No mismatches detected.
                </p>
                <p className="text-sm text-gray-400">
                  All users' points are consistent.
                </p>
              </div>
            ) : (
              <>
                {/* Search */}
                <div className="relative mb-4 w-full md:w-64">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by name or phone..."
                    className="w-full rounded-lg border py-2 pl-9 pr-3 text-sm focus:border-[#c51369] focus:outline-none"
                  />
                </div>

                {/* Flagged Users */}
                <div className="space-y-3">
                  {(filtered || []).map((entry) => {
                    const sev = severityConfig[entry.severity] || severityConfig.low;
                    const isEditing = editMode === entry.user._id;

                    return (
                      <div
                        key={entry.user._id}
                        className={`rounded-xl border ${sev.border} ${sev.bg} p-4`}
                      >
                        {/* User header */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {entry.user.avatar ? (
                              <img
                                src={entry.user.avatar}
                                alt=""
                                className="h-10 w-10 rounded-full object-cover"
                              />
                            ) : (
                              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-300 text-sm font-bold">
                                {entry.user.nickName?.[0] || "?"}
                              </div>
                            )}
                            <div>
                              <p className="font-bold text-gray-800">
                                {entry.user.nickName}
                              </p>
                              <p className="text-xs text-gray-500">
                                {entry.user.phoneNumber} • ID: {entry.user._id}
                              </p>
                            </div>
                            <span
                              className={`ml-2 rounded-full px-2 py-0.5 text-xs font-bold uppercase ${sev.badge}`}
                            >
                              {sev.icon} {entry.severity}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() =>
                                handleRecalculate(
                                  entry.user._id,
                                  entry.user.nickName
                                )
                              }
                              disabled={fixingUser === entry.user._id}
                              className="flex items-center gap-1 rounded-lg bg-green-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-green-700 disabled:opacity-50"
                            >
                              {fixingUser === entry.user._id ? (
                                <BtnLoader />
                              ) : (
                                <FaSync />
                              )}
                              Recalculate
                            </button>
                            <button
                              onClick={() => {
                                if (isEditing) {
                                  setEditMode(null);
                                } else {
                                  setEditMode(entry.user._id);
                                  setEditValues({
                                    ...entry.pointBreakdown,
                                  });
                                }
                              }}
                              className="flex items-center gap-1 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-blue-700"
                            >
                              <FaPen /> {isEditing ? "Cancel" : "Manual Edit"}
                            </button>
                          </div>
                        </div>

                        {/* Mismatch details */}
                        <div className="mt-3 grid grid-cols-3 gap-3 text-center text-sm">
                          <div>
                            <p className="font-bold text-gray-700">
                              {entry.totalPoints}
                            </p>
                            <p className="text-xs text-gray-500">
                              Total Points ($inc)
                            </p>
                          </div>
                          <div>
                            <p className="font-bold text-gray-700">
                              {entry.breakdownTotal}
                            </p>
                            <p className="text-xs text-gray-500">
                              Breakdown Sum
                            </p>
                          </div>
                          <div>
                            <p className="font-bold text-gray-700">
                              {entry.dailyActivityTotal}
                            </p>
                            <p className="text-xs text-gray-500">
                              Daily Activity Sum
                            </p>
                          </div>
                        </div>

                        {/* Type mismatches */}
                        {Object.keys(entry.typeMismatches || {}).length > 0 && (
                          <div className="mt-3">
                            <p className="mb-1 text-xs font-semibold uppercase text-gray-500">
                              Mismatched Activity Types:
                            </p>
                            <div className="space-y-1">
                              {Object.entries(entry.typeMismatches).map(
                                ([type, info]) => (
                                  <div
                                    key={type}
                                    className="flex items-center justify-between rounded-lg bg-white/70 px-3 py-1.5 text-sm"
                                  >
                                    <span className="font-medium text-gray-700">
                                      {type}
                                    </span>
                                    <div className="flex items-center gap-4 text-xs">
                                      <span>
                                        Breakdown:{" "}
                                        <b>{info.breakdownValue}</b>
                                      </span>
                                      <span>
                                        Daily:{" "}
                                        <b>{info.dailyActivitySum}</b>
                                      </span>
                                      <span
                                        className={`font-bold ${
                                          info.difference > 0
                                            ? "text-red-600"
                                            : "text-green-600"
                                        }`}
                                      >
                                        {info.difference > 0 ? "+" : ""}
                                        {info.difference.toFixed(1)} extra
                                      </span>
                                    </div>
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        )}

                        {/* Manual edit form */}
                        {isEditing && (
                          <div className="mt-3 rounded-lg border border-blue-200 bg-white p-3">
                            <p className="mb-2 text-xs font-semibold text-blue-600">
                              ✏️ Set correct point values:
                            </p>
                            <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                              {Object.entries(editValues).map(
                                ([type, value]) => (
                                  <div key={type}>
                                    <label className="text-xs text-gray-500">
                                      {type}
                                    </label>
                                    <input
                                      type="number"
                                      value={value}
                                      onChange={(e) =>
                                        setEditValues({
                                          ...editValues,
                                          [type]: Number(e.target.value),
                                        })
                                      }
                                      className="w-full rounded border px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
                                    />
                                  </div>
                                )
                              )}
                            </div>
                            <button
                              onClick={() =>
                                handleManualSave(
                                  entry.user._id,
                                  entry.user.nickName
                                )
                              }
                              disabled={fixingUser === entry.user._id}
                              className="mt-2 rounded-lg bg-blue-600 px-4 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                            >
                              {fixingUser === entry.user._id ? (
                                <BtnLoader />
                              ) : (
                                "Save Manual Points"
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default DiagnoseDoubleCountsModal;
